'use client';
import React, { HTMLAttributes, useMemo } from 'react';
import parser, {
  DOMNode,
  HTMLReactParserOptions,
  domToReact,
} from 'html-react-parser';
import Slider from 'react-slick';
import Image from 'next/image';
import Link from 'next/link';

import { IHomeDictionary } from '@/dictionaries/pages/home/types';

import './styles.css';
import { AnimatedLink } from '@/components/AnimatedLink';

interface IHomeFeaturesProps extends HTMLAttributes<HTMLDivElement> {
  dict: IHomeDictionary;
  lang: string;
}

export const HomeFeatures: React.FC<IHomeFeaturesProps> = ({ dict, lang }) => {
  const parseOptions: HTMLReactParserOptions = {
    replace: (node, index) => {
      if (node.type === 'tag' && node.name === 'a' && node.children) {
        const href = node.attribs?.href || '/';
        return (
          <Link
            className="styled-link"
            href={lang === 'en' ? `${href || '/'}` : `/${lang}${href || '/'}`}
            key={index}>
            <strong>
              {domToReact(node.children as DOMNode[], parseOptions)}
            </strong>
          </Link>
        );
      }
    },
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const featureIcons = useMemo(
    () => [
      'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/features/features-quality@3x.png',
      'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/features/features-service@3x.png',
      'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/features/features-team@3x.png',
      'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/features/guarantee@3x.png',
      'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/features/peace-of-mind@3x.png',
    ],
    [],
  );

  const features = useMemo(() => {
    return dict.features.items.map((item, index) => (
      <div className="w-full text-center" key={index}>
        <Image
          src={featureIcons[index]}
          alt={item.title}
          width={64}
          height={64}
          className="mb-6 mx-auto"
          sizes="(max-width: 1000px) 25vw, 10vw"
          style={{
            objectFit: 'contain',
          }}
        />
        <strong className="text-subtitle mb-2.5 text-gray-900">
          {item.title}
        </strong>
        <p className="desktop:my-3 text-gray-900 w-full desktop:px-8 break-words whitespace-normal">
          {item.body}
        </p>
      </div>
    ));
  }, [dict.features.items, featureIcons]);

  return (
    <section className="max-w-default mx-auto pt-32 flex w-full flex-col laptop:space-y-6 relative">
      <div className="absolute bg-gray-100 w-[120vw] left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2 h-[60%] z-0 -rotate-[8deg]" />
      <div className="grid grid-cols-1 lg:grid-cols-3 items-start w-full flex-row mx-0 relative text-center lg:text-start">
        <div className="flex items-center desktop:items-start self-start my-0 lg:w-2/3 lg:py-5 mb-4 lg:mb-0">
          <h2 className="text-title font-title font-bold">
            {parser(dict.features.title, parseOptions)}
          </h2>
        </div>
        {dict.features.items.map((item, index) => (
          <div
            className="w-full flex-col items-center desktop:items-start hidden h-0 py-0 lg:flex laptop:h-fit px-4 laptop:py-5"
            key={index}>
            <Image
              src={featureIcons[index]}
              alt={item.title}
              width={64}
              height={64}
              className="mb-6"
              sizes="(max-width: 1000px) 25vw, 10vw"
              style={{
                objectFit: 'contain',
              }}
            />
            <strong className="text-subtitle">{item.title}</strong>
            <p className="mt-3 text-gray-900">{item.body}</p>
          </div>
        ))}
      </div>

      <div className="relative flex flex-col justify-center w-full lg:hidden mb-16 py-12">
        <Slider {...settings} className="home-feat-carousel visible w-full">
          {features}
        </Slider>
      </div>

      <div className="flex justify-center mb-6">
        <AnimatedLink
          href={dict.features.cta.href}
          target="_blank"
          className="w-fit py-[20px] px-[10px] mt-[30px] desktop:px-[40px] desktop:w-fit">
          {dict.features.cta.title}
        </AnimatedLink>
      </div>
    </section>
  );
};
